import * as React from 'react';

import {
  AssetFillState,
  AssetStatus,
  enumAssetFillState,
  enumAssetStatus,
  Weight,
} from '@rematter/frontend-lib-api-client';
import { useTranslations } from '@rematter/modules/transportation/i18n/useTranslations';
import {
  Badge,
  Button,
  ContentCard,
  ContentInfo,
  ContentSection,
  Loading,
  TextField,
  WeightField,
  cn,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucideCheck, LucidePencil } from 'lucide-react';

import * as assetDetailState from '../../../_state/assetDetail.state';
import { $updateRemoteAsset } from '../../../_state/assetEditing.state';

import { SelectAccount } from '~/shared/components/select-fields/SelectAccount';
import { SelectEnum } from '~/shared/components/select-fields/SelectEnum';
import { SelectFacility } from '~/shared/components/select-fields/SelectFacility';
import { keymap } from '~/shared/keymap';

type AssetEditFormState = {
  identifier?: string;
  description?: string;
  status?: AssetStatus;
  fillState?: AssetFillState;
  tareWeight?: Weight | null;
  accountId?: string | null;
  facilityId?: string | null;
};

export const AssetTabDetails = () => {
  const t = useTranslations();
  const toast = useToast();
  const asset = useAtomValue(assetDetailState.$selectedAsset);
  const updateAsset = useSetAtom($updateRemoteAsset);

  const [isEditing, setIsEditing] = React.useState(false);
  const [form, setForm] = React.useState<AssetEditFormState>({});

  const handleStartEditing = () => {
    if (asset) {
      setForm({
        identifier: asset.identifier,
        description: asset.description,
        status: asset.status,
        fillState: asset.fillState,
        tareWeight: asset.tareWeight
          ? { ...asset.tareWeight, __typename: 'Weight' as const }
          : null,
        accountId: asset.account?.id ?? null,
        facilityId: asset.facility?.id ?? null,
      });
      setIsEditing(true);
    }
  };

  const handleCancelEditing = () => {
    setIsEditing(false);
    setForm({});
  };

  const handleSaveEditing = async () => {
    if (!asset) return;

    try {
      await updateAsset({
        id: asset.id,
        identifier: form.identifier,
        description: form.description,
        status: form.status,
        fillState: form.fillState,
        tareWeight: form.tareWeight
          ? { value: form.tareWeight.value, unit: form.tareWeight.unit }
          : undefined,
        accountId:
          form.status === enumAssetStatus.Deployed ? form.accountId : null,
        facilityId:
          form.status === enumAssetStatus.Inventory ? form.facilityId : null,
      });
      setIsEditing(false);
      toast.success(t.assetsView.detail.messages.updateSuccess);
    } catch {
      toast.error(t.assetsView.detail.messages.error);
    }
  };

  const handleFieldChange =
    <K extends keyof AssetEditFormState>(field: K) =>
    (value: AssetEditFormState[K]) => {
      setForm((prev) => ({ ...prev, [field]: value }));
    };

  const handleStatusChange = (newStatus: AssetStatus | undefined) => {
    setForm((prev) => {
      const next = { ...prev, status: newStatus };
      if (newStatus === enumAssetStatus.Deployed) {
        next.facilityId = null;
      } else if (newStatus === enumAssetStatus.Inventory) {
        next.accountId = null;
      } else {
        next.accountId = null;
        next.facilityId = null;
      }
      return next;
    });
  };

  const canSubmitForm = !!(form.identifier && form.status && form.fillState);

  return (
    <div className={cn('@AssetTabDetails', 'flex h-full flex-col gap-3 p-5')}>
      <Loading isLoading={!asset}>
        <ContentCard>
          <ContentSection
            title={
              <div className="flex items-center justify-between">
                <div>{t.assetsView.detail.sections.info}</div>
                <div
                  className={cn(
                    'flex items-center gap-6',
                    isEditing && 'pointer-events-none opacity-0',
                  )}
                >
                  <Button
                    variant="link"
                    className="px-0 text-sm font-medium"
                    preventFocus
                    icon={<LucidePencil />}
                    onClick={handleStartEditing}
                  >
                    {t.assetsView.detail.buttons.edit}
                  </Button>
                </div>
              </div>
            }
          >
            {isEditing ? (
              <div className="grid grid-cols-3 gap-4">
                <TextField
                  label={t.assetsView.form.fields.identifier}
                  autoFocus
                  value={form.identifier}
                  onChange={handleFieldChange('identifier')}
                />
                <SelectEnum<AssetStatus>
                  enumValue={enumAssetStatus}
                  label={t.assetsView.form.fields.status}
                  value={form.status}
                  onChange={handleStatusChange}
                />
                <SelectEnum<AssetFillState>
                  enumValue={enumAssetFillState}
                  label={t.assetsView.detail.fields.fillState}
                  value={form.fillState}
                  onChange={handleFieldChange('fillState')}
                />
                <TextField
                  label={t.assetsView.form.fields.description}
                  value={form.description}
                  onChange={handleFieldChange('description')}
                />
                <WeightField
                  label={t.assetsView.form.fields.tareWeight}
                  weightValue={form.tareWeight ?? undefined}
                  defaultUnit="Pounds"
                  onWeightValueChange={handleFieldChange('tareWeight')}
                />
                {form.status === enumAssetStatus.Deployed && (
                  <SelectAccount
                    label={t.assetsView.detail.fields.account}
                    value={form.accountId ?? undefined}
                    onChange={(val) =>
                      setForm((prev) => ({ ...prev, accountId: val ?? null }))
                    }
                  />
                )}
                {form.status === enumAssetStatus.Inventory && (
                  <SelectFacility
                    label={t.assetsView.detail.fields.facility}
                    value={form.facilityId ?? undefined}
                    onChange={(val) =>
                      setForm((prev) => ({ ...prev, facilityId: val ?? null }))
                    }
                  />
                )}
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-5">
                <ContentInfo
                  label={t.assetsView.form.fields.identifier}
                  value={asset?.identifier}
                >
                  {(v) => v}
                </ContentInfo>
                <ContentInfo
                  label={t.assetsView.form.fields.type}
                  value={asset?.type?.name}
                >
                  {(v) => <Badge size="small">{v}</Badge>}
                </ContentInfo>
                <ContentInfo
                  label={t.assetsView.form.fields.description}
                  value={asset?.description}
                >
                  {(v) => v}
                </ContentInfo>
                <ContentInfo
                  label={t.assetsView.form.fields.status}
                  value={asset?.status}
                >
                  {(v) => <Badge size="small">{v}</Badge>}
                </ContentInfo>
                <ContentInfo
                  label={t.assetsView.detail.fields.fillState}
                  value={asset?.fillState}
                >
                  {(v) => <Badge size="small">{v}</Badge>}
                </ContentInfo>
                <ContentInfo
                  label={t.assetsView.form.fields.tareWeight}
                  value={asset?.tareWeight}
                >
                  {(w) => `${w?.value ?? ''} ${w?.unit ?? ''}`}
                </ContentInfo>
              </div>
            )}
          </ContentSection>
          <ContentSection title={t.assetsView.detail.sections.associations}>
            <div className="grid grid-cols-3 gap-5">
              {asset?.status === enumAssetStatus.Deployed && (
                <ContentInfo
                  label={t.assetsView.detail.fields.account}
                  value={asset?.account?.name}
                >
                  {(v) => v}
                </ContentInfo>
              )}
              {asset?.status === enumAssetStatus.Inventory && (
                <ContentInfo
                  label={t.assetsView.detail.fields.facility}
                  value={asset?.facility?.facilityName}
                >
                  {(v) => v}
                </ContentInfo>
              )}
              <ContentInfo
                label={t.assetsView.detail.fields.notes}
                value={asset?.notes}
                className="col-span-3 max-w-[600px]"
              >
                {(v) => <div className="whitespace-pre-wrap">{v}</div>}
              </ContentInfo>
            </div>
          </ContentSection>
          {isEditing && (
            <div className="col-span-3 mt-3 flex items-end justify-end gap-2 border-t border-neutral/10 pt-5">
              <Button
                preventFocus
                variant="outline"
                color="neutral"
                className="border-neutral/20"
                onClick={handleCancelEditing}
              >
                {t.assetsView.detail.buttons.cancel}
              </Button>
              <Button
                icon={<LucideCheck />}
                color="primary"
                variant="outline"
                preventFocus
                isDisabled={!canSubmitForm}
                onClick={handleSaveEditing}
                keyboardShortcut={keymap.save}
              >
                {t.assetsView.detail.buttons.save}
              </Button>
            </div>
          )}
        </ContentCard>
      </Loading>
    </div>
  );
};
